import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import withRouter from "components/Common/withRouter"
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  UncontrolledTooltip,
  Table,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  AdminLoginUser,
  GetUsers,
  LoginUser,
  UpdateUserStatus,
} from "helpers/backend_helper"
import { useLoading } from "helpers/custom_hooks"
import DeclineModal from "./DeclineModal"
import ConfirmationModal from "./ConfirmationModal"
import UserEditModal from "./UserEditModal"
import SelfDisclosureModal from "./SelfDisclosureModal"
import moment from "moment"

// Das Datenmodell unterscheidet nur "Trader" (Companies.companyCategory) von
// allen übrigen Accounts. OEM/CEM/EMS werden bei der Registrierung als eine
// gemeinsame Auswahl erfasst und lassen sich daher nicht weiter aufsplitten.
const BUSINESS_TYPE_TRADER = "Trader"
const BUSINESS_TYPE_STANDARD = "OEM/CEM/EMS"

const getBusinessType = user =>
  user.companyCategory === "Trader" ? BUSINESS_TYPE_TRADER : BUSINESS_TYPE_STANDARD

const yesNo = value => (value == "1" ? "yes" : "no")

// Textfilter für eine Spalte (Teilstring, Groß-/Kleinschreibung egal).
const FilterInput = ({ value, onChange, placeholder }) => (
  <input
    type="text"
    className="form-control form-control-sm"
    value={value ?? ""}
    placeholder={placeholder || "Filter…"}
    onChange={e => onChange(e.target.value)}
  />
)

// Auswahlfilter für eine Spalte (exakter Treffer).
const FilterSelect = ({ value, onChange, options }) => (
  <select
    className="form-select form-select-sm"
    value={value ?? ""}
    onChange={e => onChange(e.target.value)}
  >
    <option value="">All</option>
    {options.map(option => (
      <option key={option} value={option}>
        {option === "" ? "-" : option}
      </option>
    ))}
  </select>
)

const Users = props => {
  const [selectedUser, setSelectedUser] = useState()
  const [users, setUsers] = useState([])
  const [filters, setFilters] = useState({})
  const [error, setError] = useState()
  const [companyInfoModal, setUserEditModal] = useState(false)
  const [declineModal, setDeclineModal] = useState(false)
  const [acceptModal, setAcceptModal] = useState(false)
  const [selfDisclosureModal, setSelfDisclosureModal] = useState(false)
  const setLoading = useLoading()

  const onStatusUpdateClick = (user, acceptUser = false) => {
    setSelectedUser(user)

    if (acceptUser) setAcceptModal(true)
    else setDeclineModal(true)
  }

  const handleStatusUpdate = async approve => {
    setLoading(true)

    try {
      await UpdateUserStatus({
        userID: selectedUser.id,
        status: approve ? "active" : "declined",
      })
      loadData()
      setDeclineModal(false)
      setAcceptModal(false)
    } catch (error) {
      alert("An unexpected error happened!")
      console.log(error)
    }
  }

  const handleLogin = async user => {
    try {
      setLoading(true)
      const result = await AdminLoginUser(user?.id)
      const url = new URL(process.env.REACT_APP_APP_URL + "/login")
      url.searchParams.append("authUser", result)
      window.open(url, "_blank")
    } catch (err) {
      err?.response?.status == 400
        ? alert(err.response.data, setError)
        : alert("An unhandled exception happend!", setError)
    }
    setLoading(false)
  }

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    let result = await GetUsers()
    result = result.sort((a, b) => {
      const statusOrder = {
        // Wartet auf Admin-Freigabe → ganz nach oben, dort ist Handlungsbedarf.
        pending: 0,
        confirmed: 1,
        "": 2,
        active: 2,
        declined: 3,
      }

      return statusOrder[a.status] - statusOrder[b.status]
    })

    setUsers(result)
    setSelectedUser()
    setLoading(false)
  }

  const setFilter = (key, value) =>
    setFilters(prev => ({ ...prev, [key]: value }))

  const resetFilters = () => setFilters({})

  const activeFilterCount = Object.values(filters).filter(
    value => value !== "" && value !== undefined
  ).length

  const asText = value => (value ?? "").toString().toLowerCase()

  // Eindeutige Werte einer Spalte als Optionen für die Auswahlfilter.
  const optionsOf = accessor =>
    [...new Set(users.map(user => (accessor(user) ?? "").toString()))].sort()

  const matchesText = (key, value) =>
    !filters[key] || asText(value).includes(asText(filters[key]))

  const matchesExact = (key, value) =>
    !filters[key] || asText(value) === asText(filters[key])

  const filteredUsers = users.filter(
    user =>
      matchesText("name", `${user.firstname} ${user.lastname}`) &&
      matchesText("email", user.email) &&
      matchesText("phoneNumber", user.phoneNumber) &&
      matchesText("company", user.name) &&
      matchesExact("status", user.status) &&
      matchesExact("accountType", user.accountType) &&
      matchesExact("businessType", getBusinessType(user)) &&
      matchesExact("qcModule", yesNo(user.qcModule)) &&
      matchesExact("repairModule", yesNo(user.repairModule)) &&
      matchesExact("partnerCockpit", yesNo(user.partnerCockpit)) &&
      matchesExact("cpsModule", yesNo(user.cpsModule)) &&
      matchesExact("billingPeriod", user.billingPeriod) &&
      matchesText("note", user.note) &&
      matchesText("discountCode", user.discountCode)
  )

  const getRowColor = user => {
    if (user.status == "active" && !user.trialUntil) return "bg-success"

    if (user.status == "active" && moment(user.trialUntil).isAfter(moment()))
      return "bg-success"

    if (user.status == "declined") return "bg-dark"

    if (user.status == "confirmed" || user.status == "pending")
      return "bg-warning"

    if (user.status == "pending payment") return "bg-info"

    return "bg-danger"
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Users" breadcrumbItem="User List" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <div className="table-responsive">
                    <Table className="table-nowrap">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone Nr.</th>
                          <th>Company</th>
                          <th>Status</th>
                          <th>Account-Type</th>
                          <th className="d-none d-lg-table-cell">QC Module</th>
                          <th className="d-none d-lg-table-cell">
                            Repair Module
                          </th>
                          <th className="d-none d-lg-table-cell">
                            Partner Cockpit
                          </th>
                          <th className="d-none d-lg-table-cell">CPS Module</th>
                          <th className="d-none d-lg-table-cell">
                            Business Type
                          </th>
                          <th className="text-center d-none d-md-table-cell">
                            Trial Until
                          </th>
                          <th className="text-center d-none d-xl-table-cell">
                            Last Invoice
                          </th>
                          <th className="d-none d-xl-table-cell">
                            Billing Period
                          </th>
                          <th className="d-none d-xl-table-cell">Note</th>
                          <th className="d-none d-lg-table-cell">
                            Discount Code
                          </th>
                          <th
                            className="text-center"
                            style={{ minWidth: "140px" }}
                          >
                            Actions
                          </th>
                        </tr>
                        <tr>
                          <th>
                            <FilterInput
                              value={filters.name}
                              onChange={v => setFilter("name", v)}
                            />
                          </th>
                          <th>
                            <FilterInput
                              value={filters.email}
                              onChange={v => setFilter("email", v)}
                            />
                          </th>
                          <th>
                            <FilterInput
                              value={filters.phoneNumber}
                              onChange={v => setFilter("phoneNumber", v)}
                            />
                          </th>
                          <th>
                            <FilterInput
                              value={filters.company}
                              onChange={v => setFilter("company", v)}
                            />
                          </th>
                          <th>
                            <FilterSelect
                              value={filters.status}
                              onChange={v => setFilter("status", v)}
                              options={optionsOf(u => u.status)}
                            />
                          </th>
                          <th>
                            <FilterSelect
                              value={filters.accountType}
                              onChange={v => setFilter("accountType", v)}
                              options={optionsOf(u => u.accountType)}
                            />
                          </th>
                          <th className="d-none d-lg-table-cell">
                            <FilterSelect
                              value={filters.qcModule}
                              onChange={v => setFilter("qcModule", v)}
                              options={["yes", "no"]}
                            />
                          </th>
                          <th className="d-none d-lg-table-cell">
                            <FilterSelect
                              value={filters.repairModule}
                              onChange={v => setFilter("repairModule", v)}
                              options={["yes", "no"]}
                            />
                          </th>
                          <th className="d-none d-lg-table-cell">
                            <FilterSelect
                              value={filters.partnerCockpit}
                              onChange={v => setFilter("partnerCockpit", v)}
                              options={["yes", "no"]}
                            />
                          </th>
                          <th className="d-none d-lg-table-cell">
                            <FilterSelect
                              value={filters.cpsModule}
                              onChange={v => setFilter("cpsModule", v)}
                              options={["yes", "no"]}
                            />
                          </th>
                          <th className="d-none d-lg-table-cell">
                            <FilterSelect
                              value={filters.businessType}
                              onChange={v => setFilter("businessType", v)}
                              options={[
                                BUSINESS_TYPE_STANDARD,
                                BUSINESS_TYPE_TRADER,
                              ]}
                            />
                          </th>
                          <th className="d-none d-md-table-cell" />
                          <th className="d-none d-xl-table-cell" />
                          <th className="d-none d-xl-table-cell">
                            <FilterSelect
                              value={filters.billingPeriod}
                              onChange={v => setFilter("billingPeriod", v)}
                              options={optionsOf(u => u.billingPeriod)}
                            />
                          </th>
                          <th className="d-none d-xl-table-cell">
                            <FilterInput
                              value={filters.note}
                              onChange={v => setFilter("note", v)}
                            />
                          </th>
                          <th className="d-none d-lg-table-cell">
                            <FilterInput
                              value={filters.discountCode}
                              onChange={v => setFilter("discountCode", v)}
                            />
                          </th>
                          <th className="text-center">
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              disabled={activeFilterCount === 0}
                              onClick={resetFilters}
                            >
                              Reset
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.length === 0 && (
                          <tr>
                            <td colSpan={17} className="text-center">
                              {users.length === 0
                                ? "There are currently no users."
                                : "No users match the current filters."}
                            </td>
                          </tr>
                        )}
                        {filteredUsers.map((user, index) => (
                          <tr
                            key={index}
                            className={getRowColor(user) + " bg-opacity-25 "}
                          >
                            <td className="align-middle">
                              {user.firstname} {user.lastname}
                            </td>
                            <td className="align-middle">
                              <a
                                className="text-dark"
                                href={"mailto:" + user.email}
                              >
                                {user.email}
                              </a>
                            </td>
                            <td className="align-middle">{user.phoneNumber}</td>
                            <td className="align-middle">{user.name}</td>
                            <td className="align-middle">{user.status}</td>
                            <td className="align-middle">{user.accountType}</td>
                            <td className="align-middle d-none d-lg-table-cell">
                              {user.qcModule == "1" ? "yes" : "no"}
                            </td>
                            <td className="align-middle d-none d-lg-table-cell">
                              {user.repairModule == "1" ? "yes" : "no"}
                            </td>
                            <td className="align-middle d-none d-lg-table-cell">
                              {user.partnerCockpit == "1" ? "yes" : "no"}
                            </td>
                            <td className="align-middle d-none d-lg-table-cell">
                              {user.cpsModule == "1" ? "yes" : "no"}
                            </td>
                            <td className="align-middle d-none d-lg-table-cell">
                              {user.companyCategory === "Trader" ? (
                                <>
                                  {BUSINESS_TYPE_TRADER}
                                  <i
                                    id={"self-disclosure-" + index}
                                    className="bx bx-file font-size-16 ms-2 text-primary"
                                    role="button"
                                    onClick={_ => {
                                      setSelectedUser(user)
                                      setSelfDisclosureModal(true)
                                    }}
                                  />
                                  <UncontrolledTooltip
                                    target={"self-disclosure-" + index}
                                  >
                                    Show self-disclosure questionnaire
                                  </UncontrolledTooltip>
                                </>
                              ) : (
                                BUSINESS_TYPE_STANDARD
                              )}
                            </td>
                            <td className="align-middle text-center d-none d-md-table-cell">
                              {user.trialUntil
                                ? moment(user.trialUntil).format("DD.MM.YYYY")
                                : "-"}
                            </td>
                            <td className="align-middle text-center d-none d-xl-table-cell">
                              {user.lastInvoice
                                ? moment(user.lastInvoice).format("DD.MM.YYYY")
                                : "-"}
                            </td>
                            <td className="align-middle d-none d-xl-table-cell">
                              {user.billingPeriod}
                            </td>

                            <td className="align-middle d-none d-xl-table-cell">
                              {user.note}
                            </td>
                            <td className="align-middle d-none d-lg-table-cell">
                              {user.discountCode || "-"}
                            </td>

                            <td className="align-middle text-center">
                              <div className="d-flex flex-column flex-md-row gap-1 justify-content-center">
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={_ => {
                                    setSelectedUser(user)
                                    setUserEditModal(true)
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  className="btn btn-dark btn-sm"
                                  onClick={_ => {
                                    handleLogin(user)
                                  }}
                                >
                                  Login
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <UserEditModal
        user={selectedUser}
        modal={companyInfoModal}
        close={_ => {
          loadData()
          setUserEditModal(false)
        }}
      />
      <SelfDisclosureModal
        user={selectedUser}
        modal={selfDisclosureModal}
        close={_ => setSelfDisclosureModal(false)}
      />
    </React.Fragment>
  )
}

export default withRouter(Users)
